import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus} from 'platform/components';

import {useCreateVehicleMutation} from '@dms/api/vehicle';
import {useCreateServiceVehicleMutation} from '@dms/api/workshop';
import {
  ServiceVehicle,
  ServiceVehicleValidationSchema,
  toAPIVehicle,
  useAdditionalNote,
  useGetCarFeatures,
  useVehicleDocumentSync,
  VehicleForm as VFKit,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {formatDocumentTitle, handleApiError} from '@dms/shared';

import {noop, useNavigate} from 'shared/web';

import {ServiceVehicleForm} from '../components/ServiceVehicleForm';
import {CREATE_SERVICE_VEHICLE_DEFAULT_VALUES} from '../constants/CREATE_SERVICE_VEHICLE_DEFAULT_VALUES';

export function CreateServiceVehicle() {
  const navigate = useNavigate();

  const [createVehicleMutation, {isLoading: isCreatingVehicle}] = useCreateVehicleMutation();
  const [createServiceVehicle, {isLoading: isServiceVehicleCreating}] =
    useCreateServiceVehicleMutation();
  const {handleSaveNote} = useAdditionalNote();
  const [getCarFeatures] = useGetCarFeatures();
  const {syncDocuments} = useVehicleDocumentSync();

  // const {
  //   generateSchema,
  //   isLoading: isMandatoryFieldsLoading,
  //   isMandatoryFieldsEnabled,
  // } = useMandatoryFields();

  // const [formSchema, setFormSchema] = useState<ZodSchema<ServiceVehicle>>(() =>
  //   generateSchema(ServiceVehicleValidationSchema, 'VEHICLETYPE_PASSENGER_CAR')
  // );

  // const handleVehicleTypeChange = useCallback(
  //   (type: VehicleTypeEnum) => {
  //     const newSchema = generateSchema(ServiceVehicleValidationSchema, type);
  //     setFormSchema(newSchema);
  //   },
  //   [generateSchema]
  // );

  const handleSave = async (values: ServiceVehicle) => {
    const sourcingData = await getCarFeatures(values);
    const apiPayload = toAPIVehicle(values, null, sourcingData);

    await createVehicleMutation({
      createVehicleRequestBody: apiPayload,
      validateMandatoryFields: false,
    })
      .unwrap()
      // Handle service vehicle creation
      .then(async (vehicle) => {
        await createServiceVehicle({
          vehicleId: vehicle.id,
          vehicleClassId: values.vehicleClassId ?? undefined,
        }).unwrap();
        return vehicle;
      })
      // Handle additional note
      .then(async (vehicle) => {
        if (values.additionalNote) {
          await handleSaveNote(vehicle.id, values.additionalNote);
        }
        return vehicle;
      })
      // Sync battery certificate document
      .then(async (vehicle) => {
        await syncDocuments({
          vehicleId: vehicle.id,
          contextTarget: 'vehicle',
          fileFields: [
            {
              fieldValue: values.batteryCertificateDocument,
              documentNote: i18n.t('entity.vehicle.labels.batteryCertificate'),
            },
          ],
        });
        return vehicle;
      })
      .then((vehicle) => {
        navigate(vehiclesRoutes.detail.replace(':id', vehicle.id));
      })
      .catch(handleApiError);
  };

  const isCreating = isCreatingVehicle || isServiceVehicleCreating;

  return (
    <>
      <Helmet>
        <title>
          {formatDocumentTitle(
            i18n.t('entity.vehicle.labels.createServiceVehicle'),
            i18n.t('entity.vehicle.labels.pageTitle')
          )}
        </title>
      </Helmet>
      <DataStatus isLoading={false}>
        <VFKit.Form
          mode="onBlur"
          experimentalZodSchema={ServiceVehicleValidationSchema}
          defaultValues={CREATE_SERVICE_VEHICLE_DEFAULT_VALUES}
        >
          <ServiceVehicleForm
            onSave={handleSave}
            onDiscard={() => navigate(-1)}
            isCreating={isCreating}
            onVehicleTypeChange={noop}
          />
        </VFKit.Form>
      </DataStatus>
    </>
  );
}
